import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { cutoutMaskStyles, useAlphaMask } from '@/lib/alphaMask';
import { ZoomIn, ZoomOut, Maximize, Magnet } from 'lucide-react';
import { Button } from '@/components/ui';
import type { MockupBlendMode } from '@/api/products';
import { DEFAULT_MOCKUP_LAYER_ORDER } from '@designdeck/shared';
import {
  applyDragToArea,
  moveArea as moveAreaMath,
  resizeArea as resizeAreaMath,
  snapAreaToCenter,
  SNAP_SCREEN_PX,
  type DragMode,
} from './printAreaMath';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export interface EditableArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface EditorOverlay {
  id: string;
  url: string;
  blendMode: MockupBlendMode;
  opacity: number;
  visible: boolean;
}

interface PrintAreaEditorProps {
  /** Resolved base mockup image URL (Layer 1) */
  baseImageUrl: string | null;
  /** Native mockup dimensions in px (coordinate space of the area) */
  mockupWidth: number;
  mockupHeight: number;
  /** Layer 0: simulated product color rendered under the base image */
  backgroundColor?: string | null;
  /** Solid colour painted behind the mockup (shows through its transparent
   * areas, so the garment contour stands out). null = transparency
   * checkerboard. */
  stageBackground?: string | null;
  /** Optional overlay layers rendered above the base image */
  overlays?: EditorOverlay[];
  /** Layer 5: cutout mask applied via CSS mask (luminance), like the creator */
  cutoutMaskUrl?: string | null;
  /** Whether the cutout mask is applied to the preview */
  maskEnabled?: boolean;
  /** The design area in native mockup px */
  area: EditableArea;
  /** Visual rotation of the area in degrees (applied around its center) */
  rotation?: number;
  /** Render a sample design inside the area (Layer 2 preview) */
  showSampleDesign?: boolean;
  /** Allow resizing the area (default true). When false the area can only
   * be moved. */
  resizable?: boolean;
  /** The height follows the width (the caller derives it from the print
   * area's millimetres, R8): the top and bottom handles are gone, corners
   * keep the ratio and Alt+arrows change the width only. */
  lockHeight?: boolean;
  /** The area is a start suggestion, not a stored frame: drawn in the
   * warning colour with a note that only a save applies it. */
  suggestion?: boolean;
  /** Render order of the layer stack, bottom → top
   * (entries: "base", "design", "heathered", "texture"; default from
   * @designdeck/shared puts the shading base above the design) */
  layerOrder?: readonly string[];
  /** Opacity of the base image (the shading map, Shirtigo "Deckkraft Schatten-Layer") */
  baseOpacity?: number;
  /** CSS max-height of the scrollable stage (default '70vh'). Lower it when
   * the editor shares a dense, no-scroll screen. */
  maxHeight?: string;
  /** How the mockup is sized at zoom 1: 'width' fits the available width (it
   * may scroll vertically), 'contain' fits the whole mockup into the stage box
   * so nothing is cut off and no zoom is needed. Default 'width'. */
  fitMode?: 'width' | 'contain';
  /** Keep the start aspect ratio while resizing from a corner handle; hold
   * Shift to resize freely. Default false (free resize). */
  proportionalResize?: boolean;
  /** Offer magnetic centre snapping while moving, plus a toggle button (hold
   * Alt to bypass). Default false so the plain per-product editor keeps its
   * original free-move behaviour; the frame-review walkthrough opts in. */
  enableMagnet?: boolean;
  /** Draws the product itself at the given display size, in place of the
   * editor's own layer stack (backgroundColor, overlays, cutout mask, layer
   * order and base opacity are then ignored). Lets a caller show the mockup
   * exactly as the creator's MockupRenderer does; the sample design and the
   * frame stay on top. */
  renderMockup?: (size: { width: number; height: number }) => ReactNode;
  /** Where the sample design sits inside the area, in mockup px relative to
   * the area's top-left corner (e.g. the carry-over box). Default: the whole
   * area. */
  sampleDesignBox?: SampleDesignBox | null;
  /** Prefix of the size chip above the area, naming what the frame is. */
  areaLabel?: string;
  /** With a sampleDesignBox: outline that box and tag it with this label, so
   * the box stands out inside the area. */
  sampleDesignBoxLabel?: string;
  /** Tag the area and the sample design box with their chips. Off keeps the
   * bare outlines, so the mockup underneath stays readable. */
  showLabels?: boolean;
  onChange: (area: EditableArea) => void;
}

/** Modifier keys of a pointer event that steer a drag. */
type DragKeys = Pick<
  React.PointerEvent,
  'altKey' | 'shiftKey' | 'metaKey' | 'ctrlKey'
>;

export interface SampleDesignBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface DragState {
  mode: DragMode;
  startClientX: number;
  startClientY: number;
  startArea: EditableArea;
}

const ZOOM_LEVELS = [0.5, 0.75, 1, 1.5, 2, 3];
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

/** Map Prisma blend modes to CSS mix-blend-mode values */
const BLEND_MODE_CSS: Record<MockupBlendMode, string> = {
  NORMAL: 'normal',
  MULTIPLY: 'multiply',
  SCREEN: 'screen',
  OVERLAY: 'overlay',
  SOFT_LIGHT: 'soft-light',
  HARD_LIGHT: 'hard-light',
};

const HANDLES: { mode: DragMode; className: string; cursor: string }[] = [
  {
    mode: 'nw',
    className: 'left-0 top-0 -translate-x-1/2 -translate-y-1/2',
    cursor: 'nwse-resize',
  },
  {
    mode: 'n',
    className: 'left-1/2 top-0 -translate-x-1/2 -translate-y-1/2',
    cursor: 'ns-resize',
  },
  {
    mode: 'ne',
    className: 'right-0 top-0 translate-x-1/2 -translate-y-1/2',
    cursor: 'nesw-resize',
  },
  {
    mode: 'e',
    className: 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2',
    cursor: 'ew-resize',
  },
  {
    mode: 'se',
    className: 'right-0 bottom-0 translate-x-1/2 translate-y-1/2',
    cursor: 'nwse-resize',
  },
  {
    mode: 's',
    className: 'left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2',
    cursor: 'ns-resize',
  },
  {
    mode: 'sw',
    className: 'left-0 bottom-0 -translate-x-1/2 translate-y-1/2',
    cursor: 'nesw-resize',
  },
  {
    mode: 'w',
    className: 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2',
    cursor: 'ew-resize',
  },
];

/**
 * Visual editor for a position's design area.
 * Renders the mockup layer stack and a draggable/resizable rectangle.
 * All coordinates are kept in the mockup's native pixel space.
 *
 * Interaction: drag to move, handles to resize, arrow keys to nudge
 * (Shift = 10px steps, Alt+arrows = resize), zoom controls for precision.
 */
export function PrintAreaEditor({
  baseImageUrl,
  mockupWidth,
  mockupHeight,
  backgroundColor = null,
  stageBackground = null,
  overlays = [],
  cutoutMaskUrl = null,
  maskEnabled = false,
  area,
  rotation = 0,
  showSampleDesign = false,
  resizable = true,
  lockHeight = false,
  suggestion = false,
  layerOrder = DEFAULT_MOCKUP_LAYER_ORDER,
  baseOpacity = 1,
  maxHeight = '70vh',
  fitMode = 'width',
  proportionalResize = false,
  enableMagnet = false,
  renderMockup,
  sampleDesignBox = null,
  areaLabel,
  showLabels = true,
  sampleDesignBoxLabel,
  onChange,
}: PrintAreaEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [maskLoadFailed, setMaskLoadFailed] = useState(false);
  // Magnetic centre snapping (hold Alt while dragging to bypass).
  const [magnet, setMagnet] = useState(true);
  const [guides, setGuides] = useState<{ v: boolean; h: boolean }>({
    v: false,
    h: false,
  });

  // CSS mask-image requires a CORS-enabled fetch; probe the mask so a
  // blocked image degrades to an unmasked preview instead of a blank one.
  useEffect(() => {
    setMaskLoadFailed(false);
    if (!cutoutMaskUrl) return;
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onerror = () => {
      if (!cancelled) setMaskLoadFailed(true);
    };
    img.src = cutoutMaskUrl;
    return () => {
      cancelled = true;
    };
  }, [cutoutMaskUrl]);

  const maskActive = maskEnabled && !!cutoutMaskUrl && !maskLoadFailed;
  const cutoutMask = useAlphaMask(maskActive ? cutoutMaskUrl : null);

  // Track available width (and, for 'contain', the stage box height) so the
  // mockup scales with the panel. A layout effect measures synchronously
  // before paint so 'contain' fits on the first frame (no width-fit flash).
  useLayoutEffect(() => {
    const widthEl = containerRef.current;
    const heightEl = viewportRef.current;
    if (!widthEl) return;

    const measure = () => {
      const width = widthEl.clientWidth;
      if (width) setContainerWidth(width);
      if (heightEl) setViewportHeight(heightEl.clientHeight);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(widthEl);
    if (heightEl) observer.observe(heightEl);
    return () => observer.disconnect();
  }, []);

  // zoom 1 is the base fit. 'width' fills the panel width (may scroll
  // vertically); 'contain' fits the whole mockup into the stage box so
  // nothing is cut off. Higher zoom overflows with scrollbars.
  const fitScale =
    fitMode === 'contain' && viewportHeight > 0 && containerWidth > 0
      ? Math.min(containerWidth / mockupWidth, viewportHeight / mockupHeight)
      : containerWidth > 0
        ? containerWidth / mockupWidth
        : 0;
  const scale = fitScale * zoom;
  const displayWidth = mockupWidth * scale;
  const displayHeight = mockupHeight * scale;

  const zoomIn = () =>
    setZoom((z) => ZOOM_LEVELS.find((l) => l > z) ?? MAX_ZOOM);
  const zoomOut = () =>
    setZoom((z) => [...ZOOM_LEVELS].reverse().find((l) => l < z) ?? MIN_ZOOM);

  const moveArea = useCallback(
    (dx: number, dy: number) => {
      onChange(moveAreaMath(area, dx, dy, { mockupWidth, mockupHeight }));
    },
    [area, mockupWidth, mockupHeight, onChange]
  );

  const resizeArea = useCallback(
    (dw: number, dh: number) => {
      onChange(resizeAreaMath(area, dw, dh, { mockupWidth, mockupHeight }));
    },
    [area, mockupWidth, mockupHeight, onChange]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const step = event.shiftKey ? 10 : 1;
      const deltas: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0],
        ArrowRight: [step, 0],
        ArrowUp: [0, -step],
        ArrowDown: [0, step],
      };
      const delta = deltas[event.key];
      if (!delta) return;

      event.preventDefault();
      if (event.altKey) {
        if (resizable) resizeArea(delta[0], lockHeight ? 0 : delta[1]);
      } else {
        moveArea(delta[0], delta[1]);
      }
    },
    [moveArea, resizeArea, resizable, lockHeight]
  );

  const applyDrag = useCallback(
    (clientX: number, clientY: number, keys: DragKeys) => {
      const drag = dragRef.current;
      if (!drag || scale === 0) return;

      // Pointer delta converted into native mockup pixels
      const dx = (clientX - drag.startClientX) / scale;
      const dy = (clientY - drag.startClientY) / scale;

      const bounds = { mockupWidth, mockupHeight };
      // Shift resizes around the centre, proportionally; Shift+Cmd (Ctrl
      // off the Mac) around the centre on the dragged axes only. Without
      // Shift, corners keep the ratio where asked and Cmd frees them.
      const free = keys.metaKey || keys.ctrlKey;
      const fromCenter = keys.shiftKey;
      // A locked height keeps every corner drag proportional; the caller
      // derives the exact height afterwards.
      const proportional = lockHeight
        ? true
        : fromCenter
          ? !free
          : proportionalResize && !free;
      let next = applyDragToArea(
        drag.mode,
        drag.startArea,
        dx,
        dy,
        bounds,
        proportional,
        fromCenter
      );

      // Magnet only while moving (not resizing); Alt temporarily bypasses it.
      if (drag.mode === 'move' && enableMagnet && magnet && !keys.altKey) {
        const snap = snapAreaToCenter(next, bounds, SNAP_SCREEN_PX / scale);
        next = snap.rect;
        setGuides({ v: snap.snappedX, h: snap.snappedY });
      } else if (guides.v || guides.h) {
        setGuides({ v: false, h: false });
      }

      onChange(next);
    },
    [
      scale,
      mockupWidth,
      mockupHeight,
      onChange,
      enableMagnet,
      magnet,
      guides.v,
      guides.h,
      proportionalResize,
      lockHeight,
    ]
  );

  const handlePointerDown = useCallback(
    (mode: DragMode) => (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      dragRef.current = {
        mode,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startArea: area,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [area]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return;
      applyDrag(event.clientX, event.clientY, event);
    },
    [applyDrag]
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      dragRef.current = null;
      setGuides({ v: false, h: false });
      event.currentTarget.releasePointerCapture(event.pointerId);
    },
    []
  );

  const rectStyle: React.CSSProperties = {
    left: area.x * scale,
    top: area.y * scale,
    width: area.width * scale,
    height: area.height * scale,
    ...(rotation !== 0 && { transform: `rotate(${rotation}deg)` }),
  };

  // Stage background: a solid colour (e.g. neon green) makes the garment
  // contour pop; otherwise a checkerboard marks transparent areas.
  const sampleBox = sampleDesignBox ?? {
    left: 0,
    top: 0,
    width: area.width,
    height: area.height,
  };
  const sampleDesignStyle: CSSProperties = {
    left: (area.x + sampleBox.left) * scale,
    top: (area.y + sampleBox.top) * scale,
    width: sampleBox.width * scale,
    height: sampleBox.height * scale,
    // Rotate with the area, around the area's centre.
    ...(rotation !== 0 && {
      transform: `rotate(${rotation}deg)`,
      transformOrigin: `${(area.width / 2 - sampleBox.left) * scale}px ${
        (area.height / 2 - sampleBox.top) * scale
      }px`,
    }),
  };

  const stageBgStyle: React.CSSProperties = stageBackground
    ? { backgroundColor: stageBackground }
    : {
        backgroundImage:
          'linear-gradient(45deg, var(--paper-3) 25%, transparent 25%), linear-gradient(-45deg, var(--paper-3) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, var(--paper-3) 75%), linear-gradient(-45deg, transparent 75%, var(--paper-3) 75%)',
        backgroundSize: '20px 20px',
        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
      };

  // Only mention the magnet in the help line when it is actually offered.
  const magnetHint = enableMagnet
    ? ' · Magnet rastet auf die Mitte (Alt beim Ziehen = frei)'
    : '';

  return (
    <div ref={containerRef} className="relative w-full select-none">
      {/* Zoom controls */}
      <div className="mb-2 flex items-center justify-end gap-1">
        {enableMagnet && (
          <Button
            type="button"
            variant={magnet ? 'primary' : 'outline'}
            size="icon-sm"
            className="mr-auto"
            aria-pressed={magnet}
            onClick={() => setMagnet((on) => !on)}
            title={
              magnet
                ? 'Magnet an: rastet auf die Mitte ein (Alt beim Ziehen = frei)'
                : 'Magnet aus: freie Platzierung'
            }
            aria-label="Magnet zum Einrasten auf die Mitte"
          >
            <Magnet className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          onClick={zoomOut}
          disabled={zoom <= MIN_ZOOM}
          title="Zoom out"
          aria-label="Zoom out"
        >
          <ZoomOut className="h-4 w-4" aria-hidden="true" />
        </Button>
        <span className="w-12 text-center font-mono text-xs font-medium text-ink-2">
          {Math.round(zoom * 100)}%
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          onClick={zoomIn}
          disabled={zoom >= MAX_ZOOM}
          title="Zoom in"
          aria-label="Zoom in"
        >
          <ZoomIn className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          className="ml-1"
          onClick={() => setZoom(1)}
          disabled={zoom === 1}
          title={fitMode === 'contain' ? 'Einpassen' : 'Breite einpassen'}
          aria-label={fitMode === 'contain' ? 'Einpassen' : 'Breite einpassen'}
        >
          <Maximize className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      <div
        ref={viewportRef}
        className="flex overflow-auto rounded-card ring-1 ring-line"
        style={
          fitMode === 'contain'
            ? { height: maxHeight, ...stageBgStyle }
            : { maxHeight }
        }
      >
        <div
          className="relative m-auto flex-shrink-0 bg-paper"
          style={{
            width: displayWidth || undefined,
            height: displayHeight || undefined,
            ...stageBgStyle,
          }}
        >
          {renderMockup ? (
            <>
              <div className="pointer-events-none absolute inset-0">
                {renderMockup({ width: displayWidth, height: displayHeight })}
              </div>
              {showSampleDesign && scale > 0 && (
                <SampleDesign style={sampleDesignStyle} />
              )}
            </>
          ) : (
            <>
              {/* Masked layer stack: color → orderable stack (heathered → design →
                  base shading → texture by default) → shadow (the design area
                  rect stays outside so it is never clipped by the mask) */}
              <div
                className="absolute inset-0"
                style={
                  maskActive && cutoutMask
                    ? cutoutMaskStyles(cutoutMask)
                    : undefined
                }
              >
                {/* Layer 0: simulated product color */}
                {backgroundColor && (
                  <div
                    className="absolute inset-0"
                    style={{ backgroundColor }}
                    data-testid="color-layer"
                  />
                )}

                {/* Orderable layer stack, bottom → top per layerOrder —
                    mirrors the creator's MockupRenderer. "base" is the shading
                    map of the garment and sits above the design by default. */}
                {layerOrder.map((layer) => {
                  if (layer === 'base') {
                    if (!baseImageUrl) return null;
                    return (
                      <img
                        key="base"
                        src={baseImageUrl}
                        alt="Mockup"
                        draggable={false}
                        className="absolute inset-0 h-full w-full"
                        style={{ opacity: baseOpacity }}
                      />
                    );
                  }
                  if (layer === 'design') {
                    if (!showSampleDesign || scale <= 0) return null;
                    return (
                      <SampleDesign key="design" style={sampleDesignStyle} />
                    );
                  }
                  const overlay = overlays.find(
                    (o) => o.id === layer && o.visible
                  );
                  if (!overlay) return null;
                  return (
                    <img
                      key={overlay.id}
                      src={overlay.url}
                      alt=""
                      draggable={false}
                      className="pointer-events-none absolute inset-0 h-full w-full"
                      style={{
                        mixBlendMode: BLEND_MODE_CSS[
                          overlay.blendMode
                        ] as React.CSSProperties['mixBlendMode'],
                        opacity: overlay.opacity,
                      }}
                    />
                  );
                })}

                {/* Shadow overlay — pinned above the ordered stack, like the
                    creator's MockupRenderer */}
                {(() => {
                  const shadow = overlays.find(
                    (o) => o.id === 'shadow' && o.visible
                  );
                  if (!shadow) return null;
                  return (
                    <img
                      key="shadow"
                      src={shadow.url}
                      alt=""
                      draggable={false}
                      className="pointer-events-none absolute inset-0 h-full w-full"
                      style={{
                        mixBlendMode: BLEND_MODE_CSS[
                          shadow.blendMode
                        ] as React.CSSProperties['mixBlendMode'],
                        opacity: shadow.opacity,
                      }}
                    />
                  );
                })()}
              </div>

              {/* Overlay — above the whole stack AND above the cutout mask, so
                  it lives outside the masked element and is never clipped */}
              {(() => {
                const top = overlays.find(
                  (o) => o.id === 'overlay' && o.visible
                );
                if (!top) return null;
                return (
                  <img
                    key="overlay"
                    src={top.url}
                    alt=""
                    draggable={false}
                    data-layer="overlay"
                    className="pointer-events-none absolute inset-0 h-full w-full"
                  />
                );
              })()}
            </>
          )}

          {/* Magnet guides: shown while the area snaps to a centre line */}
          {scale > 0 && guides.v && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 w-px bg-primary"
              style={{ left: displayWidth / 2 }}
              data-testid="snap-guide-v"
            />
          )}
          {scale > 0 && guides.h && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 h-px bg-primary"
              style={{ top: displayHeight / 2 }}
              data-testid="snap-guide-h"
            />
          )}

          {scale > 0 && (
            <div
              role="application"
              aria-label={`Design area: ${area.width} by ${area.height} pixels at position ${area.x}, ${area.y}. Use arrow keys to move${resizable ? ', Alt plus arrow keys to resize' : ''}.`}
              tabIndex={0}
              className={`absolute cursor-move border-2 border-dashed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                suggestion
                  ? 'border-warning bg-transparent'
                  : 'border-primary bg-primary-500/10 hover:bg-primary-500/20'
              }`}
              style={rectStyle}
              data-suggestion={suggestion || undefined}
              onPointerDown={handlePointerDown('move')}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onKeyDown={handleKeyDown}
              data-testid="print-area-rect"
            >
              {showLabels && (
                <span className="absolute -top-6 left-0 whitespace-nowrap rounded-chip bg-primary px-1.5 py-0.5 font-mono text-[10px] font-medium text-primary-fg">
                  {areaLabel ? `${areaLabel} · ` : ''}
                  {area.width} × {area.height} px @ ({area.x}, {area.y})
                </span>
              )}

              {resizable &&
                HANDLES.filter(
                  (handle) =>
                    !lockHeight || (handle.mode !== 'n' && handle.mode !== 's')
                ).map((handle) => (
                  <div
                    key={handle.mode}
                    aria-hidden="true"
                    className={`absolute h-3 w-3 rounded-pill border-2 border-primary bg-paper ${handle.className}`}
                    style={{ cursor: handle.cursor, touchAction: 'none' }}
                    onPointerDown={handlePointerDown(handle.mode)}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    data-testid={`print-area-handle-${handle.mode}`}
                  />
                ))}
            </div>
          )}
          {/* Outline of the sample design's box, above the area rect so it
              stays visible; it never takes pointer events. */}
          {showSampleDesign &&
            sampleDesignBox &&
            sampleDesignBoxLabel &&
            scale > 0 && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute border-2 border-dashed border-warning"
                style={sampleDesignStyle}
                data-testid="sample-design-box"
              >
                {showLabels && (
                  <span className="absolute -bottom-5 left-0 whitespace-nowrap rounded-chip bg-warning px-1.5 py-0.5 font-mono text-[10px] font-medium text-paper">
                    {sampleDesignBoxLabel}
                  </span>
                )}
              </div>
            )}
        </div>
      </div>

      {suggestion && (
        <p
          className="mt-2 rounded-control bg-paper-3 px-3 py-2 text-xs text-ink"
          data-testid="frame-suggestion-note"
        >
          Startvorschlag (40 % der Bildbreite, mittig): Rahmen anpassen und
          speichern, erst dann gilt er.
        </p>
      )}
      <p className="mt-2 text-xs text-mute">
        {!resizable
          ? `Ziehen zum Verschieben · Pfeile: 1 px (Shift: 10 px)${magnetHint}`
          : lockHeight
            ? `Ziehen zum Verschieben, Griffe zum Skalieren (die Höhe folgt den mm) · Shift: um die Mitte · Pfeile: 1 px (Shift: 10 px) · Alt+←/→: Breite${magnetHint}`
            : `Ziehen zum Verschieben, Griffe zum Skalieren${proportionalResize ? ' (proportional, ⌘ = frei)' : ''} · Shift: um die Mitte · Shift+⌘: nur diese Achse um die Mitte · Pfeile: 1 px (Shift: 10 px) · Alt+Pfeile: Größe${magnetHint}`}
      </p>
      {!renderMockup && maskEnabled && cutoutMaskUrl && maskLoadFailed && (
        <p className="mt-1 rounded-control bg-warning px-3 py-2 text-xs text-ink">
          Cutout mask could not be applied (image not CORS-accessible) — preview
          is shown without the mask.
        </p>
      )}
    </div>
  );
}

/** Height/width of the placeholder artwork: its viewBox hugs the artwork, so
 * a box of this ratio outlines the motif itself, as the creator's selection
 * frame does. */
export const SAMPLE_DESIGN_RATIO = 190 / 170;

/** Placeholder artwork shown inside the area to judge its size. */
function SampleDesign({ style }: { style: CSSProperties }) {
  return (
    <svg
      viewBox="15 23 170 190"
      preserveAspectRatio="xMidYMid meet"
      className="pointer-events-none absolute"
      style={style}
      data-testid="sample-design"
    >
      <circle cx="100" cy="95" r="70" fill="#0EA5E9" opacity="0.9" />
      <path
        d="M100 45 L113 80 L150 80 L120 102 L131 138 L100 116 L69 138 L80 102 L50 80 L87 80 Z"
        fill="#FACC15"
      />
      <text
        x="100"
        y="205"
        textAnchor="middle"
        textLength="166"
        lengthAdjust="spacingAndGlyphs"
        fontFamily="sans-serif"
        fontWeight="bold"
        fontSize="28"
        fill="#111827"
      >
        Your Design
      </text>
    </svg>
  );
}
